import React, { useEffect, useMemo, useState } from 'react';
import { Overview } from '@ofself/shell';

// MISSIONS' HOME — the page behind the column's Mi tile.
//
// The shell owns the SHAPE (@ofself/shell <Overview>: a masthead, then bands
// of figures with their names beside them); missions owns which facts belong
// on its front door.
//
// The bands run in the app's own argument: TODAY, what is actually in front
// of you; THE LATTICE, what you are trying to do; THIS WEEK, what you have
// closed. The nav column reads plan-before-present (Missions above Now) —
// the ledger is the other way round on purpose, because a page you open on
// arrival answers "what is in front of me" first and shows the reason behind
// it second.
//
// THE ARITHMETIC IS THE NOW PAGE'S OWN. The buckets come from the legacy
// engine's `bucketForItem` / `isHidden` / `isDone` on `window` — the very
// functions the Now page counts with — rather than a second implementation
// here. A home page whose "due today" disagrees with the page it links to is
// worse than no home page, and two copies of a date rule drift the first time
// one of them learns about scheduled_at.
const TODAY_KEYS = ['today', 'overdue', 'week', 'general'];

function bucketCounts(actions, habits) {
  const bucketOf = window.bucketForItem;
  const hidden = window.isHidden || (() => false);
  const done = window.isDone || (() => false);
  const counts = { today: 0, overdue: 0, week: 0, general: 0, doneToday: 0 };
  if (typeof bucketOf !== 'function') return counts;
  // The Now page's own de-duplication: a habit due today already stands in
  // `actions` as its instance, so its definition card is dropped there and
  // must be dropped here, or every daily habit is counted twice.
  const withInstance = new Set(actions.map((a) => a.parent_habit_id).filter(Boolean));
  const items = [...actions, ...habits.filter((h) => !withInstance.has(h.id))].filter((it) => !hidden(it));
  for (const it of items) {
    const b = bucketOf(it);
    if (!TODAY_KEYS.includes(b)) continue;
    if (b === 'today' && done(it)) { counts.doneToday += 1; continue; }
    if (done(it)) continue;
    counts[b] += 1;
  }
  return counts;
}

const go = (fn) => () => window[fn]?.();

export default function MissionsOverview({ className }) {
  // WAIT FOR THE ENGINE, then read the API THROUGH IT. Two reasons, and the
  // second is why this page read zeros in a group:
  //
  //   · its bucket functions are how the queue is counted (above), and on the
  //     boot route this page renders before app.js has run;
  //   · its `fetchJson` is what puts the SCOPE on a request — ?group_id=… for
  //     a group, plus the X-Timezone header the server needs to know which day
  //     "today" is. A bare fetch() asks the personal scope in the server's own
  //     timezone, which in a group answers with an empty world.
  //
  // So the one seam is app.js's own fetch, not a second one that looks like it.
  const [engine, setEngine] = useState(() => !!window.__missionsEngineReady);
  useEffect(() => {
    if (engine) return undefined;
    const ready = () => setEngine(true);
    document.addEventListener('missions:engine-ready', ready);
    return () => document.removeEventListener('missions:engine-ready', ready);
  }, [engine]);

  const [now, setNow] = useState(null);
  const [missions, setMissions] = useState(null);
  const [week, setWeek] = useState(null);
  // Bumped every time home is ENTERED (navToHome). At boot the engine resolves
  // the URL's scope after the groups land and re-dispatches the route, so the
  // first read can be one scope behind — this is the signal that the scope has
  // settled, and it costs nothing when it hasn't moved.
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    const again = () => setEpoch((n) => n + 1);
    document.addEventListener('missions:home', again);
    return () => document.removeEventListener('missions:home', again);
  }, []);

  useEffect(() => {
    if (!engine || typeof window.fetchJson !== 'function') return undefined;
    let live = true;
    const get = (url) => window.fetchJson(url).catch(() => null);
    get('/api/now').then((d) => live && d && setNow(d));
    get('/api/missions').then((d) => live && d && setMissions(d.missions || []));
    // Last and never blocking: the week's tally is the heaviest read here and
    // the least urgent thing on the page. Its lines print quiet zeros until it
    // lands, which is what a zero looks like anyway.
    get('/api/lookback').then((d) => live && d && setWeek(d.stats || null));
    return () => { live = false; };
  }, [engine, epoch]);

  const counts = useMemo(
    () => bucketCounts(now?.actions || [], now?.habits || []),
    [now, engine],
  );
  const open = useMemo(
    () => (missions || []).filter((m) => m.status !== 'completed' && m.status !== 'archived'),
    [missions],
  );
  const habitsKept = (now?.habits || []).filter((h) => h.status !== 'paused').length;

  return (
    <Overview className={className}>
      <Overview.Group label="Today">
        <Overview.Line figure={counts.overdue} label="overdue" onPress={go('navToNow')} />
        <Overview.Line figure={counts.today} label="due today" onPress={go('navToNow')} />
        <Overview.Line figure={counts.doneToday} label="done today" onPress={go('navToNow')} />
        <Overview.Line figure={counts.week} label="this week" note="the next seven days" onPress={go('navToSchedule')} />
      </Overview.Group>

      <Overview.Group label="The lattice">
        <Overview.Line figure={open.length} label={open.length === 1 ? 'mission' : 'missions'} onPress={go('navToMissions')} />
        <Overview.Line figure={habitsKept} label="habits" note="you keep" onPress={go('navToNow')} />
        <Overview.Line figure={counts.general} label="untimed" note="no date yet" onPress={go('navToNow')} />
      </Overview.Group>

      <Overview.Group label="This week">
        <Overview.Line figure={week?.actions_completed ?? 0} label="completed" onPress={go('navToLookback')} />
        <Overview.Line figure={week?.goals_closed ?? 0} label={week?.goals_closed === 1 ? 'goal closed' : 'goals closed'} onPress={go('navToLookback')} />
        <Overview.Line figure={week?.items_skipped ?? 0} label="skipped" onPress={go('navToLookback')} />
      </Overview.Group>
    </Overview>
  );
}
